// drag-drop-list.component.ts
import { Component, computed, effect, Inject, inject, Optional, signal, ElementRef, NgZone } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { LayerService } from '../services/layer.service';
import { MapStatusService } from '../services/map-status.service';
import { OfflineLayerService } from '../services/offline-layer.service';
import { DrawStyleService } from '../services/draw-style.service';
import { DgmCalibrationService } from '../services/dgm-calibration.service';
import { GridStyle, LayerDetails } from '../types';
import { PROJECT_PROVIDER, DOWNLOAD_PROGRESS_COMPONENT, AUTH_PROVIDER, AuthProvider, GEODATA_API_SERVICE, IGeodataApiService } from '../../core';
import { MAP_NAVIGATION_HANDLER, MapNavigationHandler } from '../handlers';
import { GridControlsComponent } from '../controls/grid-controls/grid-controls.component';
import { OfflineModeService } from '../../services/offline-mode.service';
import { ProjectPointsService } from './project-points.service';
import { firstValueFrom, filter } from 'rxjs';
import { HttpEventType, HttpResponse } from '@angular/common/http';
import { ZeichnungslayerDefinition } from '../types/zeichnungslayer.types';
import { DrawStyleModalComponent } from '../controls/draw-style/draw-style-modal.component';
import { StorageService } from '../../services/storage/storage.service';

interface SubLayer {
	key: string;
	title: string;
}

interface ListItem {
  id: number;
  text: string;
  type: string;
	ld: LayerDetails;
	sublayers?: SubLayer[];
	removable?: boolean;
	visible?: boolean;
	isGeodata?: boolean;
	noDownloadStatus?: boolean;  // Layer hat keinen Download-Status (z.B. dynamisch generiert)
	gpkgSubLayer?: string;
	wmsSubLayer?: string;  // WMS-Sublayer-Title für individuelle Sichtbarkeit
	// Für Zeichnungslayer
	isZeichnungslayer?: boolean;
	zeichnungslayerId?: string;
	zeichnungslayer?: ZeichnungslayerDefinition;
	// Für interne Layer (Punkte)
	isInternalLayer?: boolean;
	internalLayerId?: string;
	// Für Grid-Layer
	isGridLayer?: boolean;
	gridStyle?: GridStyle;
	gridResolution?: number;
	// Zoom-abhängige Sichtbarkeit (aus geojsonStyle)
	styleMinZoom?: number;
	styleMaxZoom?: number;
}

@Component({
  selector: 'bp-project-layers',
  standalone: true,
  imports: [CommonModule, FormsModule, GridControlsComponent],
  templateUrl: 'project-layers.component.html',
  styleUrls: ['project-layers.component.css']
})
export class ProjectLayersComponent {
  private modalService = inject(NgbModal);
  private elementRef = inject(ElementRef);
  private ngZone = inject(NgZone);
  private navigationHandler = inject(MAP_NAVIGATION_HANDLER, { optional: true }) as MapNavigationHandler | null;

  items: ListItem[] = [];

  draggedItem: ListItem | null = null;
  isDragging = false;

	// Download-Status pro Layer (Map von Layer-Name zu Status)
	layerDownloadStatus = signal<Map<string, 'none' | 'partial' | 'complete'>>(new Map());

	// Status für Geodaten-Dateien (true = vorhanden)
	geodataFileStatus = signal<Map<string, boolean>>(new Map());

	// True wenn Download-Status initial ermittelt wurde
	statusInitialized = signal<boolean>(false);

	// Zeichnungslayer
	zeichnungslayer = signal<ZeichnungslayerDefinition[]>([]);

	editingLayerId = signal<string | null>(null);

	// Inline-Rename
	renamingItemId = signal<number | null>(null);

	// Punkte-Layer Sichtbarkeit
	pointsLayerVisible = signal<boolean>(true);
	// Flächen-Layer Sichtbarkeit (vorerst deaktiviert)
	polygonsLayerVisible = signal<boolean>(false);

	private readonly geodataTypes = new Set(['gpkg', 'geotiff', 'geojson']);
	// Layer-Typen die keinen Download-Status haben (dynamisch generiert)
	private readonly noDownloadStatusTypes = new Set(['grid']);

	// Flag um während Drag & Drop den effect zu ignorieren
	private isDraggingOrder = false;

	private drawStyleService = inject(DrawStyleService);
	private mapStatus = inject(MapStatusService);
	private storage = inject(StorageService);
	private auth = inject(AUTH_PROVIDER) as AuthProvider;
	private pps = inject(ProjectPointsService);
	private geodataApi = inject(GEODATA_API_SERVICE, { optional: true }) as IGeodataApiService | null;
	dgmCalibration = inject(DgmCalibrationService);

	/** Prüft ob ein WMS-Sublayer ein DGM-Layer ist (kalibriert oder unkalibriert) */
	isDgmLayer(item: ListItem): boolean {
		if (!this.dgmCalibration.available()) return false;
		const url = (item.ld?.url || '').toLowerCase();
		const isDgm = url.includes('dgm_wms') || url.includes('dgm/service');
		if (!isDgm) return false;
		const name = (item.wmsSubLayer || item.text || '').toLowerCase();
		return name.includes('hoehendarstellung') || name.includes('höhendarstellung') || name.includes('höhenfarben');
	}

	/** Startet die DGM-Farbkalibrierung für das aktuelle Projektgebiet */
	async calibrateDgm(): Promise<void> {
		const regions = this.ls.regions()?.regions ?? [];
		if (regions.length === 0) {
			alert('Keine Projektbereiche definiert. Bitte zuerst Bereiche festlegen.');
			return;
		}

		// BBOX aus allen Regionen berechnen
		let minLat = Infinity, maxLat = -Infinity, minLng = Infinity, maxLng = -Infinity;
		for (const r of regions) {
			const b = r.bounds as any;
			const sw = b._southWest || b;
			const ne = b._northEast || b;
			if (sw.lat < minLat) minLat = sw.lat;
			if (ne.lat > maxLat) maxLat = ne.lat;
			if (sw.lng < minLng) minLng = sw.lng;
			if (ne.lng > maxLng) maxLng = ne.lng;
		}
		const bbox = `${minLng},${minLat},${maxLng},${maxLat}`;
		console.log('[DGM-Kalibrierung] BBOX:', bbox, 'Regions:', regions.length);

		try {
			await this.dgmCalibration.calibrate(bbox);
			this.ls.layersChanged.emit();
		} catch (err: any) {
			alert('Kalibrierung fehlgeschlagen: ' + (err.message || err));
		}
	}

	/** Setzt die DGM-Kalibrierung zurück */
	async resetDgmCalibration(): Promise<void> {
		await this.dgmCalibration.reset();
		this.ls.layersChanged.emit();
	}

	/** Prüft ob ein Layer wegen Zoom-Einschränkung gerade ausgeblendet ist */
	isZoomHidden(item: ListItem): boolean {
		if (item.styleMinZoom == null && item.styleMaxZoom == null) return false;
		const zoom = this.mapStatus.currentZoom();
		if (item.styleMinZoom != null && zoom < item.styleMinZoom) return true;
		if (item.styleMaxZoom != null && zoom > item.styleMaxZoom) return true;
		return false;
	}

	isGuest(): boolean {
		return this.auth.amI('gast');
	}
	readonly guestHint = 'Für Gäste nicht verfügbar';

	constructor(private ls: LayerService, private ols: OfflineLayerService, @Inject(PROJECT_PROVIDER) private ps: any, public oms: OfflineModeService, @Inject(DOWNLOAD_PROGRESS_COMPONENT) private downloadProgressComponent: any) {
		// Synchronisiere editingLayerId mit activeDrawLayerId wenn Zeichentool aktiv ist
		effect(() => {
			const activeId = this.ls.activeDrawLayerId();
			const toolActive = this.ls.isDrawToolActive();

			if (toolActive && activeId) {
				// Zeichentool ist aktiv - editingLayerId synchronisieren
				this.editingLayerId.set(activeId);
			} else if (!toolActive && this.editingLayerId() !== null) {
				// Zeichentool wurde beendet - editingLayerId zurücksetzen
				this.editingLayerId.set(null);
			}
		});

		effect(() => {
			// mapDef()-Abhängigkeit registrieren
			const mapDef = this.ls.mapDef();

			// Während Drag & Drop nicht neu laden
			if (this.isDraggingOrder) return;

			// DGM-Kalibrierung aus Projekt laden
			this.dgmCalibration.loadFromProject();

			this.statusInitialized.set(false);
			this.items = [];
			let index = 0;
			mapDef?.layers.forEach((ld) => {
				if (ld.display === undefined)
					ld.display = true;

				// GPKG: Jeden Sublayer als separaten Eintrag anzeigen (ohne Dateiname)
				if (ld.type === 'gpkg' && ld.layers && ld.layers.length > 0) {
					ld.layers.forEach((layer) => {
						if (!layer.enabled) return;
						const item = {
							id: index++,
							text: layer.title || layer.name,
							type: ld.type,
							ld: ld,
							removable: true,
							visible: false,
							isGeodata: true,
							gpkgSubLayer: layer.name
						} as ListItem;
						this.items.push(item);
					});
					return;
				}

				// WMS: Jeden aktivierten Sublayer als separaten Eintrag anzeigen
				if (ld.type === 'wms' && ld.layers && ld.layers.some(l => l.enabled)) {
					ld.layers.forEach((layer) => {
						if (!layer.enabled) return;
						// DGM-Schummerung (intern) nicht als eigenen Eintrag anzeigen
						if (layer.title === '__dgm_schummerung__') return;
						const item = {
							id: index++,
							text: layer.title || layer.name,
							type: ld.type,
							ld: ld,
							removable: true,
							visible: ld.display !== false,
							wmsSubLayer: layer.title || layer.name
						} as ListItem;
						this.items.push(item);
					});
					return;
				}

				const item = {
						id: index++,
						text: ld.name,
						type: ld.type,
						ld: ld,
						removable: ld.type !== 'osm',
						visible: ld.display !== false,
						isGeodata: this.geodataTypes.has(ld.type),
						noDownloadStatus: this.noDownloadStatusTypes.has(ld.type),
						isGridLayer: ld.type === 'grid',
						gridStyle: ld.gridStyle,
						gridResolution: ld.resolution
					} as ListItem;
				// GeoJSON: Zoom-Einschränkungen aus gespeichertem Style laden
				if (ld.type === 'geojson') {
					const gStyle = this.storage.getSync(`geojsonStyle_${ld.name}`);
					if (gStyle) {
						item.styleMinZoom = gStyle.minZoom;
						item.styleMaxZoom = gStyle.maxZoom;
					}
				}
				if (ld.layers) {
					item.sublayers = [];

					ld.layers?.forEach((layer) => {
						if (!layer.enabled) return;
						item.sublayers?.push({
							key: layer.name,
							title: layer.title || layer.name
						});
					});
				}
				this.items.push(item);
			});

			// GPKG-Sublayer mit gleichem Namen aus verschiedenen Dateien disambiguieren
			const gpkgItems = this.items.filter(i => i.gpkgSubLayer);
			const textCounts = new Map<string, number>();
			for (const item of gpkgItems) {
				textCounts.set(item.text, (textCounts.get(item.text) || 0) + 1);
			}
			for (const item of gpkgItems) {
				if ((textCounts.get(item.text) || 0) > 1) {
					const filename = item.ld.name.replace(/\.gpkg$/i, '');
					item.text = `${filename}: ${item.text}`;
				}
			}

			this.items.reverse();
			this._updateVisibilityFromHiddenLayers();
			// Zeichnungslayer nach dem Laden der normalen Layer hinzufügen
			this.integrateZeichnungslayer();

			Promise.all([
				this.updateAllDownloadStatus(),
				this.updateGeodataStatus()
			]).then(() => {
				this.statusInitialized.set(true);
			});
		});
		this._updateVisibilityFromHiddenLayers();

		// Status aktualisieren wenn Seeding abgeschlossen
		this.ols.seedAllCompleted.subscribe(() => this.updateAllDownloadStatus());

		// Status aktualisieren wenn Attachments geändert wurden
		this.ls.attachmentsChanged.subscribe(() => this.updateGeodataStatus());

		// Zeichnungslayer bei Änderungen aktualisieren
		this.ls.zeichnungslayerChanged.subscribe(() => this.integrateZeichnungslayer());
		// Bei komplettem Reload (z.B. nach Aktualisieren): Zeichnungslayer neu laden
		this.ls.zeichnungslayerReloadRequested.subscribe(() => this.integrateZeichnungslayer());
	}

	private async updateAllDownloadStatus(): Promise<void> {
		const newStatusMap = new Map<string, 'none' | 'partial' | 'complete'>();
		// Nur aktive Bereiche für Status-Berechnung (konsistent mit Download)
		const allRegions = this.ls.regions()?.regions ?? [];
		const activeIndices = this.pps.activeRegionIndices();
		const activeRegions = activeIndices.length > 0
			? activeIndices.filter(i => i < allRegions.length).map(i => allRegions[i])
			: allRegions;
		const regionBounds = activeRegions.map(r => r.bounds);
		for (const item of this.items) {
			// OSM-Layer: z7-z11 kommen aus MBTiles, Download erst ab z12
			const minDownloadZoom = item.ld.type === 'osm' ? 12 : undefined;
			if (item.sublayers && item.sublayers.length > 0) {
				for (const sublayer of item.sublayers) {
					// maxNativeZoom ist auf dem Parent-Layer (item.ld)
					const status = await this.ols.getLayerDownloadStatus(sublayer.title, regionBounds, item.ld.maxNativeZoom, minDownloadZoom);
					newStatusMap.set(sublayer.title, status);
				}
			} else {
				const status = await this.ols.getLayerDownloadStatus(item.text, regionBounds, item.ld.maxNativeZoom, minDownloadZoom);
				newStatusMap.set(item.text, status);
			}
		}
		this.layerDownloadStatus.set(newStatusMap);
	}

	getDownloadStatus(item: ListItem): 'none' | 'partial' | 'complete' {
		if (item.sublayers && item.sublayers.length > 0) {
			// Für WMS-Layer mit Sublayers: aggregiere den Status
			let hasAny = false;
			let allComplete = true;
			for (const sublayer of item.sublayers) {
				const status = this.layerDownloadStatus().get(sublayer.title) ?? 'none';
				if (status !== 'none') hasAny = true;
				if (status !== 'complete') allComplete = false;
			}
			if (!hasAny) return 'none';
			if (allComplete) return 'complete';
			return 'partial';
		}
		return this.layerDownloadStatus().get(item.text) ?? 'none';
	}

	isSeeding(item: ListItem): boolean {
		if (item.sublayers && item.sublayers.length > 0) {
			return item.sublayers.some(sl => this.ols.isSeeding(sl.title));
		}
		return this.ols.isSeeding(item.text);
	}

	downloadLayer(item: ListItem): void {
		// Fortschritts-Dialog öffnen
		const progressModalRef = this.modalService.open(this.downloadProgressComponent, {
			backdrop: 'static',
			keyboard: false
		});
		progressModalRef.componentInstance.skipTileDownload = false;
		progressModalRef.componentInstance.skipPointsDownload = true;

		// Download starten
		if (item.sublayers && item.sublayers.length > 0) {
			for (const sublayer of item.sublayers) {
				this.ols.seedLayerRequest.emit(sublayer.title);
			}
		} else {
			this.ols.seedLayerRequest.emit(item.text);
			// DGM kalibriert: auch Schummerung-Layer herunterladen
			if (this.isDgmLayer(item) && this.dgmCalibration.settings()) {
				this.ols.seedLayerRequest.emit('__dgm_schummerung__');
			}
		}
	}

	private async updateGeodataStatus(): Promise<void> {
		const statusMap = new Map<string, boolean>();
		for (const item of this.items) {
			if (item.isGeodata) {
				const exists = await this.ps.getProjectValue(item.ld.name, 'attachments');
				statusMap.set(item.ld.name, !!exists);
			}
		}
		this.geodataFileStatus.set(statusMap);
	}

	isGeodataPresent(item: ListItem): boolean {
		return this.geodataFileStatus().get(item.ld.name) ?? false;
	}

	async restoreMissingGeodata(item: ListItem): Promise<void> {
		// Zuerst Server versuchen (wenn verfügbar und online)
		if (this.geodataApi && this.oms.networkOnline()) {
			try {
				const projectId = this.ps.project();
				const response = await firstValueFrom(
					this.geodataApi.download(projectId, item.ld.name).pipe(
						filter(e => e.type === HttpEventType.Response)
					)
				);
				const buf = (response as HttpResponse<ArrayBuffer>).body;
				if (buf) {
					if (item.ld.name.endsWith('.geojson') || item.ld.name.endsWith('.json')) {
						const text = new TextDecoder().decode(buf);
						await this.ps.setProjectValue(item.ld.name, text, 'attachments');
					} else {
						await this.ps.setProjectValue(item.ld.name, buf, 'attachments');
					}
					await this.ps.addGeodataFile(item.ld.name);
					this.ls.attachmentsChanged.emit();
					await this.updateGeodataStatus();
					return;
				}
			} catch {
				// Server hat die Datei nicht — File-Picker als Fallback
			}
		}

		// Fallback: Datei manuell auswählen
		const success = await this.ls.addMissingAttachment(item.ld.name);
		if (success) {
			await this.updateGeodataStatus();
		}
	}

	private _updateVisibilityFromHiddenLayers() {
		this.ls.hiddenLayers().then((hiddenLayers: string[]) => {
			const hiddenSet = new Set(hiddenLayers);
			this.items.forEach((item) => {
				// GPKG-Sublayer: Prüfe vollen Key "dateiname_sublayer"
				if (item.gpkgSubLayer) {
					const fullKey = `${item.ld.name}_${item.gpkgSubLayer}`;
					item.visible = !hiddenSet.has(fullKey);
				} else if (item.wmsSubLayer) {
					const fullKey = `${item.ld.name}_${item.wmsSubLayer}`;
					item.visible = !hiddenSet.has(fullKey);
				} else {
					item.visible = !hiddenSet.has(item.ld.name);
				}
			});
			this.ls.layerVisibilityChanged.emit();
		});
	}

	toggleVisibility(item: ListItem) {
		item.visible = !item.visible;
		if (item.gpkgSubLayer) {
			this.ls.toggleVisibility(item.ld, item.gpkgSubLayer);
		} else if (item.wmsSubLayer) {
			this.ls.toggleVisibility(item.ld, item.wmsSubLayer);
		} else {
			this.ls.toggleVisibility(item.ld);
		}
	}

	hasBounds(item: ListItem): boolean {
		return !!item.ld.bbox;
	}

	zoomToLayer(item: ListItem): void {
		this.ls.zoomToLayerRequested.emit(item.ld.name);
	}

	zoomToGrid(): void {
		this.ls.gridZoomRequested.emit();
	}

  onDragStart(event: DragEvent, item: ListItem): void {
    const target = event.target as HTMLElement;
    const gripperIcon = target.closest('[data-gripper]');
    if (!gripperIcon) {
      event.preventDefault();
      return;
    }
    this.draggedItem = item;
    this.isDragging = true;

    // Kleines Timeout, damit das Element noch sichtbar ist beim Start
    setTimeout(() => {
      if (event.dataTransfer) {
        event.dataTransfer.effectAllowed = 'move';
      }
    }, 0);
  }

  onDragEnd(event: DragEvent): void {
    if (this.isDragging && this.draggedItem) {
      // Reihenfolge speichern wenn sich etwas geändert hat
      this.saveLayerOrder();
    }
    this.draggedItem = null;
    this.isDragging = false;
    this.dragEnterDebounce = false;
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = 'move';
    }
  }

  private dragEnterDebounce = false;

  onDragEnter(event: DragEvent, targetItem: ListItem): void {
    event.preventDefault();
    if (!this.draggedItem || this.draggedItem.id === targetItem.id) return;

    // Entprellung: Während Animation keine neue Umordnung
    if (this.dragEnterDebounce) return;
    this.dragEnterDebounce = true;

    const draggedIndex = this.items.findIndex(item => item.id === this.draggedItem!.id);
    const targetIndex = this.items.findIndex(item => item.id === targetItem.id);

    if (draggedIndex === -1 || targetIndex === -1) {
      this.dragEnterDebounce = false;
      return;
    }

    // FLIP Animation: Positionen vor der Änderung speichern
    const listItems = this.elementRef.nativeElement.querySelectorAll('[data-item-id]');
    const firstPositions = new Map<string, DOMRect>();
    listItems.forEach((el: HTMLElement) => {
      const id = el.getAttribute('data-item-id');
      if (id) firstPositions.set(id, el.getBoundingClientRect());
    });

    // Items umordnen
    const newItems = [...this.items];
    newItems.splice(draggedIndex, 1);
    newItems.splice(targetIndex, 0, this.draggedItem);
    this.items = newItems;

    // FLIP Animation: Nach dem Rendern animieren
    this.ngZone.runOutsideAngular(() => {
      requestAnimationFrame(() => {
        const updatedItems = this.elementRef.nativeElement.querySelectorAll('[data-item-id]');
        updatedItems.forEach((el: HTMLElement) => {
          const id = el.getAttribute('data-item-id');
          if (!id) return;

          const first = firstPositions.get(id);
          const last = el.getBoundingClientRect();
          if (!first) return;

          const deltaY = first.top - last.top;
          if (Math.abs(deltaY) < 1) return;

          // Sofort an alte Position setzen
          el.style.transform = `translateY(${deltaY}px)`;
          el.style.transition = 'none';

          // Dann zur neuen Position animieren
          requestAnimationFrame(() => {
            el.style.transition = 'transform 150ms ease-out';
            el.style.transform = '';
          });
        });

        // Entprellung nach Animation zurücksetzen
        setTimeout(() => {
          this.dragEnterDebounce = false;
        }, 150);
      });
    });
  }

  onDrop(event: DragEvent, targetItem: ListItem): void {
    event.preventDefault();
    // Die Umordnung passiert bereits in onDragEnter, hier nur noch speichern
    if (this.draggedItem) {
      this.saveLayerOrder();
    }
    this.draggedItem = null;
    this.isDragging = false;
    this.dragEnterDebounce = false;
  }

  /**
   * Speichert die Reihenfolge aller Layer (normale + Zeichnungslayer gemischt)
   * Nutzt saveLayerOrderOnly() um keinen kompletten Map-Rebuild auszulösen
   */
  private async saveLayerOrder(): Promise<void> {
    // Flag setzen um effect zu blockieren während wir speichern
    this.isDraggingOrder = true;

    try {
      // Normale Layer speichern (ohne Zeichnungslayer und interne Layer, für Kompatibilität)
      const normalLayers = this.items.filter(i => !i.isZeichnungslayer && !i.isInternalLayer);
      // Nutze saveLayerOrderOnly statt saveLayersToLocalStorage um keinen Rebuild zu triggern
      this.ls.saveLayerOrderOnly({ layers: normalLayers.map(i => i.ld).reverse() });

      // Gesamte Reihenfolge speichern: Layer-Name, Zeichnungslayer-ID oder interne Layer-ID
      // GPKG/WMS-Sublayer: "layer:dateiname_sublayer"
      const fullOrder = this.items.map(i => {
        if (i.isInternalLayer) return i.internalLayerId!;
        if (i.isZeichnungslayer) return i.zeichnungslayerId!;
        if (i.gpkgSubLayer) return `layer:${i.ld.name}_${i.gpkgSubLayer}`;
        if (i.wmsSubLayer) return `layer:${i.ld.name}_${i.wmsSubLayer}`;
        return `layer:${i.ld.name}`;
      });

      // Event für Z-Order-Änderung sofort mit der neuen Reihenfolge auslösen
      this.ls.layerOrderChanged.emit(fullOrder);

      // Dann async speichern
      await this.ps.setProjectValue('layerOrder', fullOrder);
    } finally {
      // Flag zurücksetzen
      this.isDraggingOrder = false;
    }
  }

  onTouchStart(event: TouchEvent, item: ListItem): void {
    const target = event.target as HTMLElement;
    const gripperIcon = target.closest('[data-gripper]');
    if (!gripperIcon) return;

    event.preventDefault();
    event.stopPropagation();
    this.draggedItem = item;
    this.isDragging = true;
  }

  onTouchMove(event: TouchEvent): void {
    if (!this.isDragging) return;
    event.preventDefault();
    event.stopPropagation();

    const touch = event.touches[0];
    const elements = document.elementsFromPoint(touch.clientX, touch.clientY);
    const listItem = elements.find(el => el.hasAttribute('data-item-id'));

    if (listItem) {
      const targetId = parseInt(listItem.getAttribute('data-item-id')!);
      const targetItem = this.items.find(item => item.id === targetId);

      if (targetItem && this.draggedItem && targetItem.id !== this.draggedItem.id) {
        const draggedIndex = this.items.findIndex(item => item.id === this.draggedItem!.id);
        const targetIndex = this.items.findIndex(item => item.id === targetId);

        const newItems = [...this.items];
        newItems.splice(draggedIndex, 1);
        newItems.splice(targetIndex, 0, this.draggedItem);
        this.items = newItems;
      }
    }
  }

  onTouchEnd(event: TouchEvent): void {
    // Nur preventDefault wenn wirklich gedraggt wurde, sonst werden Button-Clicks blockiert
    if (this.isDragging && event) {
      event.preventDefault();
      event.stopPropagation();
      // Reihenfolge speichern nach Touch-Drag
      this.saveLayerOrder();
    }
    this.isDragging = false;
    this.draggedItem = null;
  }

  onContainerTouchMove(event: TouchEvent): void {
    if (this.isDragging) {
      event.preventDefault();
    }
  }

	// === Zeichnungslayer-Verwaltung ===

	/**
	 * Integriert Zeichnungslayer und interne Layer in die items-Liste
	 * Jeder Zeichnungslayer wird einzeln angezeigt (keine virtuellen __lines__/__polygons__ mehr)
	 * Die Reihenfolge kann mit normalen Layern gemischt werden
	 */
	private async integrateZeichnungslayer(): Promise<void> {
		const layers = await this.ps.getProjectValue('zeichnungslayer') as ZeichnungslayerDefinition[] | null;
		this.zeichnungslayer.set(layers ?? []);

		// Gespeicherte Gesamtreihenfolge laden
		const savedOrder = await this.ps.getProjectValue('layerOrder') as string[] | null;

		// Punkte-Sichtbarkeit laden
		const pointsVisible = await this.ps.getProjectValue('pointsLayerVisible') as boolean | null;
		this.pointsLayerVisible.set(pointsVisible !== false); // Standard: sichtbar

		// Flächen-Layer: Sichtbarkeit aus Projekteinstellungen laden (Standard: unsichtbar)
		const polygonsVisible = await this.ps.getProjectValue('polygonsLayerVisible') as boolean | null;
		this.polygonsLayerVisible.set(polygonsVisible === true);

		// Zuletzt aktiven Zeichnungslayer laden und ins Signal setzen
		const lastActiveId = await this.ps.getProjectValue('lastActiveDrawLayerId') as string | null;
		if (lastActiveId) {
			// Prüfen ob der Layer noch existiert und sichtbar ist
			const layerExists = (layers ?? []).some(l => l.id === lastActiveId && l.visible !== false);
			if (layerExists) {
				this.ls.lastActiveDrawLayerId.set(lastActiveId);
			}
		}

		// Vorhandene Zeichnungslayer und interne Layer aus items entfernen
		this.items = this.items.filter(item => !item.isZeichnungslayer && !item.isInternalLayer);

		let maxId = Math.max(...this.items.map(i => i.id), 0);
		const specialItemsMap = new Map<string, ListItem>();

		// Punkte-Layer immer hinzufügen
		specialItemsMap.set('__points__', {
			id: ++maxId,
			text: 'Punkte',
			type: 'internal-points',
			ld: {} as LayerDetails,
			visible: this.pointsLayerVisible(),
			isInternalLayer: true,
			internalLayerId: '__points__'
		});

		// Flächen-Layer nur anzeigen wenn vom Projekt aktiviert
		if (this.polygonsLayerVisible()) {
			specialItemsMap.set('__polygons__', {
				id: ++maxId,
				text: 'Flächen',
				type: 'internal-polygons',
				ld: {} as LayerDetails,
				visible: true,
				isInternalLayer: true,
				internalLayerId: '__polygons__'
			});
		}

		// Jeden Zeichnungslayer einzeln hinzufügen
		const allLayers = layers ?? [];
		for (const layer of allLayers) {
			specialItemsMap.set(layer.id, {
				id: ++maxId,
				text: layer.name,
				type: 'zeichnung',
				ld: {} as LayerDetails,
				visible: layer.visible,
				isZeichnungslayer: true,
				zeichnungslayerId: layer.id,
				zeichnungslayer: layer
			});
		}

		// Wenn gespeicherte Reihenfolge vorhanden, Items entsprechend sortieren
		if (savedOrder && savedOrder.length > 0) {
			// Key für GPKG/WMS-Sublayer: "layer:dateiname_sublayer", sonst "layer:name"
			const getItemKey = (i: ListItem) => {
				if (i.gpkgSubLayer) return `layer:${i.ld.name}_${i.gpkgSubLayer}`;
				if (i.wmsSubLayer) return `layer:${i.ld.name}_${i.wmsSubLayer}`;
				return `layer:${i.ld.name}`;
			};
			const normalLayersMap = new Map(this.items.map(i => [getItemKey(i), i]));
			const sortedItems: ListItem[] = [];

			for (const key of savedOrder) {
				if (key.startsWith('layer:')) {
					const item = normalLayersMap.get(key);
					if (item) {
						sortedItems.push(item);
						normalLayersMap.delete(key);
					}
				} else {
					// Spezielle Layer (__points__ oder Zeichnungslayer-ID)
					const item = specialItemsMap.get(key);
					if (item) {
						sortedItems.push(item);
						specialItemsMap.delete(key);
					}
				}
			}

			// Neue Items (nicht in gespeicherter Reihenfolge) am Anfang hinzufügen
			const remainingSpecial = Array.from(specialItemsMap.values());
			const remainingNormal = Array.from(normalLayersMap.values());
			this.items = [...remainingSpecial, ...sortedItems, ...remainingNormal];
		} else {
			// Standard: Spezielle Layer am Anfang
			this.items = [...Array.from(specialItemsMap.values()), ...this.items];
		}
	}

	async toggleInternalLayerVisibility(layerId: string): Promise<void> {
		if (layerId === '__points__') {
			const newVisible = !this.pointsLayerVisible();
			this.pointsLayerVisible.set(newVisible);
			await this.ps.setProjectValue('pointsLayerVisible', newVisible);
			const item = this.items.find(i => i.internalLayerId === layerId);
			if (item) {
				item.visible = newVisible;
			}
			this.ls.pointsLayerVisibilityChanged.emit(newVisible);
		} else if (layerId === '__polygons__') {
			const newVisible = !this.polygonsLayerVisible();
			this.polygonsLayerVisible.set(newVisible);
			await this.ps.setProjectValue('polygonsLayerVisible', newVisible);
			const item = this.items.find(i => i.internalLayerId === layerId);
			if (item) {
				item.visible = newVisible;
			}
			this.ls.polygonsLayerVisibilityChanged.emit(newVisible);
		}
	}

	openLegendModal(): void {
		if (this.navigationHandler?.legendComponent) {
			this.modalService.open(this.navigationHandler.legendComponent, { centered: true });
		}
	}

	async toggleZeichnungslayerVisibility(layerId: string): Promise<void> {
		const currentLayer = this.zeichnungslayer().find(l => l.id === layerId);
		const willBeHidden = currentLayer?.visible === true;

		const updated = this.zeichnungslayer().map(l =>
			l.id === layerId ? { ...l, visible: !l.visible } : l
		);
		await this.ps.setProjectValue('zeichnungslayer', updated);
		this.zeichnungslayer.set(updated);
		const item = this.items.find(i => i.zeichnungslayerId === layerId);
		if (item) {
			item.visible = !item.visible;
			// zeichnungslayer Referenz aktualisieren
			const updatedLayer = updated.find(l => l.id === layerId);
			if (updatedLayer) {
				item.zeichnungslayer = updatedLayer;
			}
		}
		this.ls.zeichnungslayerVisibilityChanged.emit(layerId);

		// Wenn der Layer ausgeblendet wird und gerade editiert wird, Bearbeitungsmodus beenden
		if (willBeHidden && this.editingLayerId() === layerId) {
			this.ls.zeichnungslayerEditRequested.emit(null);
			this.editingLayerId.set(null);
			this.ls.activeDrawLayerId.set(null);
		}
	}

	toggleEdit(layerId: string): void {
		// Exklusive Aktivierung: Wenn dieser Layer aktiv ist, deaktivieren
		// Sonst diesen Layer aktivieren (und alle anderen implizit deaktivieren)
		if (this.editingLayerId() === layerId) {
			this.ls.zeichnungslayerEditRequested.emit(null);
			this.editingLayerId.set(null);
			this.ls.activeDrawLayerId.set(null);
			// lastActiveDrawLayerId bleibt erhalten, damit der Layer beim nächsten Tool-Start wieder aktiviert wird
		} else {
			this.ls.zeichnungslayerEditRequested.emit(layerId);
			this.editingLayerId.set(layerId);
			this.ls.activeDrawLayerId.set(layerId);
			// Als zuletzt aktiven Layer merken und persistieren
			this.ls.lastActiveDrawLayerId.set(layerId);
			this.ps.setProjectValue('lastActiveDrawLayerId', layerId);
		}
	}

	isEditing(layerId: string): boolean {
		return this.editingLayerId() === layerId;
	}

	isLastActive(layerId: string): boolean {
		return this.ls.lastActiveDrawLayerId() === layerId && !this.isEditing(layerId);
	}

	zoomToZeichnungslayer(layerId: string): void {
		this.ls.zeichnungslayerZoomRequested.emit(layerId);
	}

	// === Zeichenstil ===

	getDrawStrokeColor(item: ListItem): string {
		return item.zeichnungslayer?.strokeColor ?? '#3388ff';
	}

	getDrawFillColor(item: ListItem): string {
		const hex = item.zeichnungslayer?.fillColor ?? '#3388ff';
		const opacity = item.zeichnungslayer?.fillOpacity ?? 0.2;
		// Hex zu RGB konvertieren
		const r = parseInt(hex.slice(1, 3), 16);
		const g = parseInt(hex.slice(3, 5), 16);
		const b = parseInt(hex.slice(5, 7), 16);
		return `rgba(${r}, ${g}, ${b}, ${opacity})`;
	}

	openStyleModal(item: ListItem): void {
		if (!item.zeichnungslayer) return;

		const modalRef = this.modalService.open(DrawStyleModalComponent, {
			centered: true,
			size: 'md'
		});
		// Layer-Daten an Modal übergeben
		modalRef.componentInstance.layerId = item.zeichnungslayerId;
		modalRef.componentInstance.layerStyle = {
			strokeColor: item.zeichnungslayer.strokeColor,
			strokeWidth: item.zeichnungslayer.strokeWidth,
			fillColor: item.zeichnungslayer.fillColor,
			fillOpacity: item.zeichnungslayer.fillOpacity
		};

		// Bei Schließen Style aktualisieren
		modalRef.result.then(async (newStyle) => {
			if (newStyle && item.zeichnungslayerId) {
				const updated = this.zeichnungslayer().map(l =>
					l.id === item.zeichnungslayerId ? { ...l, ...newStyle } : l
				);
				await this.ps.setProjectValue('zeichnungslayer', updated);
				this.zeichnungslayer.set(updated);
				// Item aktualisieren
				if (item.zeichnungslayer) {
					item.zeichnungslayer = { ...item.zeichnungslayer, ...newStyle };
				}
				this.ls.zeichnungslayerChanged.emit();
			}
		}).catch(() => {});
	}

	/**
	 * Gibt den gespeicherten Style für einen GPKG-Sublayer zurück.
	 */
	getGpkgLayerStyle(item: ListItem): { color: string, fillColor: string, fillOpacity: number } {
		if (!item.gpkgSubLayer || !item.ld) {
			return { color: '#666', fillColor: 'transparent', fillOpacity: 0 };
		}

		const fullLayerName = `${item.ld.name}_${item.gpkgSubLayer}`;
		const storageKey = `gpkgStyle_${fullLayerName}`;
		const savedStyle = this.storage.getSync(storageKey);

		if (savedStyle) {
			return {
				color: savedStyle.color || '#666',
				fillColor: savedStyle.fillColor || 'transparent',
				fillOpacity: savedStyle.fillOpacity || 0
			};
		}

		// Default-Farben basierend auf Layer-Name
		const name = item.gpkgSubLayer.toLowerCase();
		if (name.includes('standort') || name.includes('sto') || name.includes('lbfo') || name.includes('stgr')) {
			return { color: '#cc0000', fillColor: 'transparent', fillOpacity: 0 };
		}
		if (name.includes('humus') || name.includes('hufo') || name.includes('hum')) {
			return { color: '#228B22', fillColor: 'transparent', fillOpacity: 0 };
		}
		return { color: '#666', fillColor: 'transparent', fillOpacity: 0 };
	}

	/**
	 * Gibt die Linienfarbe für den GPKG-Layer zurück (für Vorschau-Button).
	 */
	getGpkgStrokeColor(item: ListItem): string {
		return this.getGpkgLayerStyle(item).color;
	}

	/**
	 * Gibt die Füllfarbe mit Opacity für den GPKG-Layer zurück (für Vorschau-Button).
	 */
	getGpkgFillColor(item: ListItem): string {
		const style = this.getGpkgLayerStyle(item);
		if (style.fillColor === 'transparent' || style.fillOpacity === 0) {
			return 'transparent';
		}
		// Hex zu RGBA konvertieren
		const hex = style.fillColor;
		const r = parseInt(hex.slice(1, 3), 16);
		const g = parseInt(hex.slice(3, 5), 16);
		const b = parseInt(hex.slice(5, 7), 16);
		return `rgba(${r}, ${g}, ${b}, ${style.fillOpacity})`;
	}

	/**
	 * Öffnet das Style-Modal für einen GPKG-Sublayer.
	 */
	openGpkgStyleModal(item: ListItem): void {
		if (!item.gpkgSubLayer || !item.ld) return;

		const fullLayerName = `${item.ld.name}_${item.gpkgSubLayer}`;
		const style = this.getGpkgLayerStyle(item);

		const modalRef = this.modalService.open(DrawStyleModalComponent, {
			centered: true
		});

		modalRef.componentInstance.gpkgMode = true;
		modalRef.componentInstance.gpkgLayerName = item.gpkgSubLayer;
		modalRef.componentInstance.layerStyle = {
			strokeColor: style.color,
			strokeWidth: 2,
			fillColor: style.fillColor === 'transparent' ? '#3388ff' : style.fillColor,
			fillOpacity: style.fillOpacity
		};

		modalRef.result.then((newStyle) => {
			if (newStyle) {
				// Style speichern
				const storageKey = `gpkgStyle_${fullLayerName}`;
				this.storage.setSync(storageKey, {
					color: newStyle.strokeColor,
					weight: newStyle.strokeWidth,
					fillColor: newStyle.fillColor,
					fillOpacity: newStyle.fillOpacity
				});

				// Layer-Service über Änderung informieren
				this.ls.layersChanged.emit();
			}
		}).catch(() => {});
	}

	// === GeoJSON-Layer Styling ===

	/**
	 * Gibt den gespeicherten Style für einen GeoJSON-Layer zurück.
	 */
	private getGeojsonLayerStyle(item: ListItem): { color: string; fillColor: string; fillOpacity: number } {
		const layerName = item.ld?.name || item.text;
		const storageKey = `geojsonStyle_${layerName}`;
		const savedStyle = this.storage.getSync(storageKey);

		return savedStyle || {
			color: 'darkslategray',
			fillColor: 'transparent',
			fillOpacity: 0
		};
	}

	/**
	 * Gibt die Linienfarbe für den GeoJSON-Layer zurück (für Vorschau-Button).
	 */
	getGeojsonStrokeColor(item: ListItem): string {
		return this.getGeojsonLayerStyle(item).color;
	}

	/**
	 * Gibt die Füllfarbe mit Opacity für den GeoJSON-Layer zurück (für Vorschau-Button).
	 */
	getGeojsonFillColor(item: ListItem): string {
		const style = this.getGeojsonLayerStyle(item);
		if (style.fillColor === 'transparent' || style.fillOpacity === 0) {
			return 'transparent';
		}
		// Hex zu RGBA konvertieren
		const hex = style.fillColor;
		const r = parseInt(hex.slice(1, 3), 16);
		const g = parseInt(hex.slice(3, 5), 16);
		const b = parseInt(hex.slice(5, 7), 16);
		return `rgba(${r}, ${g}, ${b}, ${style.fillOpacity})`;
	}

	/**
	 * Öffnet das Style-Modal für einen GeoJSON-Layer.
	 */
	openGeojsonStyleModal(item: ListItem): void {
		if (!item.ld) return;

		const layerName = item.ld.name;
		const style = this.getGeojsonLayerStyle(item);

		const modalRef = this.modalService.open(DrawStyleModalComponent, {
			centered: true
		});

		modalRef.componentInstance.gpkgMode = true;
		modalRef.componentInstance.gpkgLayerName = layerName;
		modalRef.componentInstance.layerStyle = {
			strokeColor: style.color,
			strokeWidth: 2,
			fillColor: style.fillColor === 'transparent' ? '#3388ff' : style.fillColor,
			fillOpacity: style.fillOpacity
		};

		modalRef.result.then((newStyle) => {
			if (newStyle) {
				// Style speichern
				const storageKey = `geojsonStyle_${layerName}`;
				this.storage.setSync(storageKey, {
					color: newStyle.strokeColor,
					weight: newStyle.strokeWidth,
					fillColor: newStyle.fillColor,
					fillOpacity: newStyle.fillOpacity
				});

				// Layer-Service über Änderung informieren
				this.ls.layersChanged.emit();
			}
		}).catch(() => {});
	}

	// === Layer umbenennen ===

	private renameCancelled = false;

	startRename(item: ListItem): void {
		this.renamingItemId.set(item.id);
		// Input nach Render fokussieren und Text selektieren
		setTimeout(() => {
			const input = this.elementRef.nativeElement.querySelector('.rename-input') as HTMLInputElement;
			if (input) {
				input.focus();
				input.select();
			}
		});
	}

	finishRename(item: ListItem, newName: string): void {
		if (this.renameCancelled) {
			this.renameCancelled = false;
			return;
		}

		this.renamingItemId.set(null);

		const trimmed = newName?.trim();
		if (!trimmed) return;

		if (item.isZeichnungslayer && item.zeichnungslayerId) {
			// Zeichnungslayer: name in der Definition aktualisieren
			const updated = this.zeichnungslayer().map(l =>
				l.id === item.zeichnungslayerId ? { ...l, name: trimmed } : l
			);
			this.ps.setProjectValue('zeichnungslayer', updated);
			this.zeichnungslayer.set(updated);
			item.text = trimmed;
			if (item.zeichnungslayer) {
				item.zeichnungslayer = { ...item.zeichnungslayer, name: trimmed };
			}
			this.ls.zeichnungslayerChanged.emit();
		} else if (item.ld && item.ld.name) {
			// Normaler Layer: displayName setzen
			item.ld.displayName = trimmed;
			const mapDef = this.ls.mapDef();
			if (mapDef) {
				this.ls.saveLayersToLocalStorage(mapDef);
			}
		}
	}

	cancelRename(): void {
		this.renameCancelled = true;
		this.renamingItemId.set(null);
	}

	// === Grid-Layer Styling ===

	onGridStyleChange(item: ListItem, style: GridStyle): void {
		if (item.ld) {
			item.ld.gridStyle = style;
			item.gridStyle = style;
			const mapDef = this.ls.mapDef();
			if (mapDef) {
				this.ls.saveLayersToLocalStorage(mapDef);
			}
		}
	}

	onGridResolutionChange(item: ListItem, resolution: number): void {
		if (item.ld) {
			item.ld.resolution = resolution;
			item.gridResolution = resolution;
			const mapDef = this.ls.mapDef();
			if (mapDef) {
				this.ls.saveLayersToLocalStorage(mapDef);
			}
		}
	}

	onGridRadiusChange(item: ListItem, radius: number): void {
		if (item.ld && item.ld.gridStyle) {
			item.ld.gridStyle.radius = radius;
			if (item.gridStyle) item.gridStyle.radius = radius;
			const mapDef = this.ls.mapDef();
			if (mapDef) {
				this.ls.saveLayersToLocalStorage(mapDef);
			}
		}
	}

}
